/**
 * Configuration for the CodePush update dialog.
 * Mirrors CodePush.CustomDialogConfig from @code-push-next/react-native-code-push.
 *
 * Note: This dialog is only shown for mandatory updates. Users can choose to:
 * - Install now: Downloads and applies the update immediately (app restarts)
 * - Later: Downloads the update but defers application to next app restart
 */
export interface CustomDialogConfig {
  /** The title of the update dialog. */
  title: string;
  /** The message body of the update dialog. */
  message: string;
  /** Text for the install/continue button. */
  installButtonText: string;
  /** Text for the defer button. Falls back to "Later" if not provided. */
  ignoreButtonText?: string;
  /** Whether this is a mandatory update. */
  isMandatory: boolean;
  /** Call this function when the user chooses to install the update immediately. */
  onInstall: () => void;
  /** Call this function when the user chooses to defer (still downloads, applies on next restart). */
  onIgnore: () => void;
}

type Listener = (config: CustomDialogConfig | null) => void;

class CodePushDialogManagerClass {
  private config: CustomDialogConfig | null = null;
  private listeners: Set<Listener> = new Set();

  show(config: CustomDialogConfig): void {
    this.config = config;
    this.notifyListeners();
  }

  hide(): void {
    this.config = null;
    this.notifyListeners();
  }

  getConfig(): CustomDialogConfig | null {
    return this.config;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.config));
  }
}

export const CodePushDialogManager = new CodePushDialogManagerClass();
